import React, { useEffect, useState } from 'react';
import { getUserLists, importMembersCSV } from '../composables/fetchAPI';
import { Users, Shield, ShieldCheck, Eye, EyeOff, Clock, Send, XCircle } from 'lucide-react';

const DashboardPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const data = await getUserLists({q: searchQuery, page: page.toString(), limit: limit.toString(),isActive: isActiveFilter.toString() });
        setUsers(Array.isArray(data.users) ? data.users : []);
        setTotalPages(data.pageInfo?.totalPages || 1);
        setTotalUsers(data.total || 0); // <-- set real total user count
        
      } catch (error) {
        setUsers([]);
        setTotalUsers(0);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [page, limit, isActiveFilter, searchQuery]);

  // Find if current user is admin
  const currentUser = users.find(u => u.isSelf);
  const isAdmin = currentUser?.isAdmin;

  const formatLastLogin = (value: string | null | undefined) => {
    if (!value) return 'Never logged in';
    const date = new Date(value);
    return date.toLocaleString('th-TH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const telegramStatusStyles: Record<string, string> = {
    linked: 'bg-sky-900 text-sky-200 border border-sky-700',
    unlinked: 'bg-gray-700 text-gray-300 border border-gray-600',
    pending: 'bg-yellow-900 text-yellow-200 border border-yellow-700',
  };

  // Handle CSV import
  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportError(null);
    setImportSuccess(null);
    try {
      const result = await importMembersCSV(file);
      setImportSuccess('Import successful!');
      // Optionally, refresh user list
      // const data = await getUserLists();
      // setUsers(Array.isArray(data) ? data : data.users || []);
    } catch (error: any) {
      setImportError(error.message || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  // UI only: toggle status (local, not API)
  // const toggleUserStatus = (userId: string) => {
  //   setUsers(users.map(user =>
  //     user.id === userId
  //       ? { ...user, isActive: !user.isActive }
  //       : user
  //   ));
  // };

  // const activeUsers = users.filter(user => user.isActive).length;
  // const adminUsers = users.filter(user => user.isAdmin).length;

  return (
    <div className="min-h-screen bg-[#18181b] text-gray-100">
      {/* Header */}
      <div className="bg-[#18181b] border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-400" />
            <h1 className="text-2xl font-bold text-white">User Management</h1>
          </div>
          
            <div>
              <label htmlFor="csv-upload" className="relative">
                <input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  disabled={importLoading}
                  className="hidden"
                />
                <button
                  type="button"
                  className={`bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg shadow transition-colors duration-150 flex items-center ${
                    importLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onClick={() => document.getElementById('csv-upload')?.click()}
                  disabled={importLoading}
                >
                  Import Members (.csv)
                </button>
              </label>
            </div>
       
        </div>
        {/* Import status messages */}
        {importLoading && <div className="text-yellow-400 mt-2">Importing...</div>}
        {importError && <div className="text-red-400 mt-2">{importError}</div>}
        {importSuccess && <div className="text-green-400 mt-2">{importSuccess}</div>}
      </div>

      {/* User List */}
      <div className="p-6">
         <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-4 p-4  bg-gray-700 text-white w-full rounded-lg"
        />
        <div className="bg-[#18181b] rounded-lg border border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">Users ({totalUsers})</h2>
          </div>
          {loading ? (
            <div className="text-gray-300 text-lg px-6 py-8">Loading...</div>
          ) : users.length === 0 ? (
            <div className="text-gray-400 px-6 py-8">No users found.</div>
          ) : (
            <div className="divide-y divide-gray-700">
              {users.map((user: any) => (
                <div key={user.id} className="px-6 py-4 hover:bg-gray-750 transition-colors">
                  <div className="flex items-center justify-between">
                    {/* User Info */}
                    <div className="flex items-center space-x-4 flex-1">
                      <img
                        src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=0D8ABC&color=fff`}
                        alt={user.name}
                        className="w-10 h-10 rounded-full bg-gray-600"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM0Qjc0ODAiLz4KPHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1zbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBkPSJNMTIgMTJDMTQuNzYxNCAxMiAxNyA5Ljc2MTQyIDE3IDdDMTcgNC4yMzg1OCAxNC43NjE0IDIgMTIgMkM5LjIzODU4IDIgNyA0LjIzODU4IDcgN0M3IDkuNzYxNDIgOS4yMzg1OCAxMiAxMiAxMloiIGZpbGw9IndoaXRlIi8+CiAgPHBhdGggZD0iTTEyIDE0QzguNjg2MjkgMTQgNiAxNi42ODYzIDYgMjBWMjJIMThWMjBDMTggMTYuNjg2MyAxNS4zMTM3IDE0IDEyIDE0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-medium text-white">{user.name}</h3>
                          {user.isAdmin && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-900 text-yellow-200 border border-yellow-700">
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              Admin
                            </span>
                          )}
                          {user.isSelf && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-200 border border-blue-700">
                              You
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{user.email}</p>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <Clock className="w-3 h-3 mr-1" />
                          last login: {formatLastLogin(user.lastLoginAt)}
                        </div>
                      </div>
                    </div>

                    {/* Status Toggle */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-400">Telegram:</span>
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            telegramStatusStyles[user.telegramStatus] || telegramStatusStyles.unlinked
                          }`}
                        >
                          {user.telegramStatus === 'linked' ? (
                            <Send className="w-3 h-3 mr-1" />
                          ) : (
                            <XCircle className="w-3 h-3 mr-1" />
                          )}
                          {user.telegramStatus || 'unlinked'}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-400">Status:</span>
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                            user.isActive
                              ? 'bg-green-900 text-green-200 border border-green-700 hover:bg-green-800'
                              : 'bg-red-900 text-red-200 border border-red-700 hover:bg-red-800'}`}
                        >
                          {user.isActive ? (
                            <>
                              <Eye className="w-3 h-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3 h-3 mr-1" />
                              Inactive
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Pagination Controls */}
        <div className="flex justify-center items-center mt-4 space-x-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="px-3 py-1 rounded bg-gray-700 text-gray-200 disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-gray-300">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            className="px-3 py-1 rounded bg-gray-700 text-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;